import type { ReactNode } from 'react'

import type { CustomerRow } from '@/api/customers'
import { useMoney } from '@/features/overview/hooks'

import type { Paging } from '../list/ListStates'
import { ListTable } from '../list/ListTable'
import { COLUMN_CHOICE, CUSTOMER_COLUMNS, describeReason, GROUP_LABELS, unavailableReason, type CellContext } from './columns'
import type { CustomersResult } from './customersList'
import type { CustomerParams } from './params'

/** The loaded list, or a search's answer, in the shared server-sorted table (`../list/ListTable`). */
export function CustomerTable({
  result,
  params,
  identity,
  paging,
  empty,
}: {
  result: CustomersResult
  params: CustomerParams
  identity: (row: CustomerRow) => ReactNode
  /** Null for a search's answer, which is one page. */
  paging: Paging | null
  /** What an empty list says: why the list is empty, or that the search found nobody. */
  empty: ReactNode
}) {
  const { rows, latest, notAvailable } = result
  const { locale } = useMoney(latest.currency)
  const context: CellContext = { notAvailable, currency: latest.currency, locale, timezone: latest.timezone }
  return (
    <ListTable
      caption="Customers"
      columns={CUSTOMER_COLUMNS}
      groupLabels={GROUP_LABELS}
      columnChoice={COLUMN_CHOICE}
      sort={{ sort: params.sort, order: params.order }}
      onSort={params.setSort}
      rows={rows}
      rowKey={result.keyOf}
      context={context}
      cells={{ customer_ref: identity }}
      rule={unavailableReason}
      describe={describeReason}
      empty={empty}
      footer={paging ? { total: latest.total, noun: 'customers', paging } : null}
    />
  )
}
